import { Box, BoxProps, Typography, TypographyProps } from '@mui/material';
import { SentimentIcon } from 'features/socialMediaListening';
import { Sentiment } from 'graphql/generated';
import { useEffect, useState } from 'react';
import { theme } from 'styles/theme';

export type SLASentimentBarChartData = {
  chartData: SLASentimentBarChartDataItem[];
};

export type SLASentimentBarChartDataItem = {
  sentiment: Sentiment;
  percent: number;
  color: string | undefined;
};

export type SLASentimentBarChartProps = {
  data: SLASentimentBarChartData;
  barWidth?: number;
  barHeight?: number;
  renderChartIndicators?: boolean;
  componentsProps?: {
    container?: BoxProps;
    bar?: BoxProps;
    label?: TypographyProps;
  };
};

export const SLASentimentBarChart = ({
  data,
  barWidth = 42,
  barHeight = 200,
  renderChartIndicators = true,
  componentsProps,
}: SLASentimentBarChartProps) => {
  const { chartData } = data;
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <Box
      {...componentsProps?.container}
      sx={{
        display: 'flex',
        position: 'relative',
        flexDirection: 'row',
        gap: theme.spacing(15),
        ...componentsProps?.container?.sx,
      }}
    >
      {renderChartIndicators && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 50,
            bottom: 50,
            zIndex: 1,
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => {
            const bottom = 25 * index;
            return (
              <Box
                key={index}
                sx={{
                  width: '100%',
                  height: 1,
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: `${bottom}%`,
                  borderBottom: `1px dashed ${theme.colors?.utility[400]}`,
                }}
              />
            );
          })}
        </Box>
      )}
      {chartData.map((item, index) => {
        // remove the decimal digits when there’s 0%.
        const percentText = item.percent === 0 ? '0' : item.percent.toFixed(2);
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing(6),
              mt: `${barWidth}px`,
              zIndex: 2,
            }}
          >
            {/* Bar container */}
            <Box
              {...componentsProps?.bar}
              sx={{
                width: barWidth,
                height: barHeight,
                borderRadius: '100px',
                position: 'relative',
                zIndex: 1,
                ...componentsProps?.bar?.sx,
              }}
            >
              {/* Bar */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  backgroundColor: item.color,
                  borderBottomLeftRadius: barWidth,
                  borderBottomRightRadius: barWidth,
                  height: animate ? `${item.percent}%` : 0,
                  transition: 'height 1s ease',
                }}
              >
                {/* Sentiment emoji on top */}
                <Box
                  sx={{
                    height: barWidth,
                    width: barWidth,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: -barWidth,
                  }}
                >
                  <Box
                    sx={{
                      height: barWidth / 2,
                      width: barWidth,
                      bgcolor: item.color,
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 2,
                    }}
                  />
                  <SentimentIcon
                    size={barWidth}
                    sentiment={item.sentiment}
                    sx={{
                      zIndex: 3,
                    }}
                    outline={false}
                  />
                </Box>
              </Box>
            </Box>
            {/* Label */}
            <Typography
              fontSize={20}
              variant="headline-md"
              color={theme.colors?.utility[700]}
              {...componentsProps?.label}
            >
              {percentText}%
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
